import { PURGE } from 'redux-persist';
import constants from '../../constants';

let objectData = {
  views: [],
  defaultView: null,

  records: [],
  error: null,
  loading: false,
  pagination: {},
  properties: [],
  searchResults: [],

  // Import state
  importHistory: [],
  importStatus: null,
  importPreview: null,
  importWizardOpen: false,

  // Export state
  exportHistory: [],
  exportStatus: null,
  exportPreview: null,
  exportWizardOpen: false,

  // Filter state
  filters: {
    filterGroups: [],
    groupOperator: 'OR'
  },
  advanceFilter: '',
  activeFilters: null,
  filter: constants['initialAdvanceFilter'],

  // Tags state
  allTags: [],
}

const initialState = {
  deals: objectData,
  contacts: objectData,
  companies: objectData,
  agents: objectData,
  workflows: objectData,
  campaignContacts: objectData,
  properties: null,
  propertiesLoading: false,
  verifyingEmails: false,
}

export default function reducer(state = initialState, { type, payload }) {
  switch (type) {
    case PURGE:
      return initialState;

    case 'BULK_VERIFY_EMAILS':
      return { ...state, verifyingEmails: true };

    case 'BULK_VERIFY_EMAILS_DONE':
      return { ...state, verifyingEmails: false };

    case 'GET_OBJECTS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          error: null,
          loading: true,
        }
      };

    case 'SET_OBJECTS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          error: null,
          loading: false,
          records: payload['response']['records'] || [],
          properties: payload['response']['properties'] || [],
          pagination: payload['response']['pagination'] || {},
          ...(payload['response']['contactStats'] ? { contactStats: payload['response']['contactStats'] } : {})
        }
      };

    case 'SET_OBJECTS_LOADING':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          loading: payload['loading']
        }
      };

    case 'SET_OBJECTS_ERROR':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          loading: false,
          error: payload['error']
        }
      };

    case 'CREATE_OBJECT_SUCCESS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          records: [payload['object'], ...state[payload['objectType']]['records']]
        }
      };

    case 'UPDATE_OBJECT_SUCCESS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          records: state[payload['objectType']]['records'].map(obj =>
            obj['_id'] === payload['id'] ? { ...obj, ...payload['updates'] } : obj
          )
        }
      };

    case 'DELETE_OBJECT_SUCCESS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          records: state[payload['objectType']]['records'].filter(obj => obj['_id'] !== payload['id'])
        }
      };

    case 'SET_SEARCH_RESULTS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          searchResults: payload['results']
        }
      };

    case 'GET_PROPERTIES':
      return { ...state, propertiesLoading: true };

    case 'SET_PROPERTIES':
      let updatedState = { ...state };
      Object.entries(payload).forEach(([objectType, properties]) => {
        if (updatedState[objectType]) {
          // Extract allTags from the tags property's fieldConfig.options
          let allTags = [];
          if (Array.isArray(properties)) {
            properties.forEach(group => {
              if (!Array.isArray(group)) return;
              const tagsProp = group.find(p => p.name === 'tags');
              if (tagsProp?.fieldConfig?.options) {
                allTags = tagsProp.fieldConfig.options
                  .filter(opt => opt.value && opt.label)
                  .map(opt => ({ name: opt.value, color: opt.color || '#6B7280' }));
              }
            });
          } else if (properties && typeof properties === 'object') {
            Object.values(properties).forEach(group => {
              if (!Array.isArray(group)) return;
              const tagsProp = group.find(p => p.name === 'tags');
              if (tagsProp?.fieldConfig?.options) {
                allTags = tagsProp.fieldConfig.options
                  .filter(opt => opt.value && opt.label)
                  .map(opt => ({ name: opt.value, color: opt.color || '#6B7280' }));
              }
            });
          }
          updatedState[objectType] = { ...updatedState[objectType], properties, allTags };
        }
      });
      return updatedState;

    case 'SET_VIEWS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          views: payload['views']
        }
      };

    case 'TOGGLE_IMPORT_WIZARD':
      return {
        ...state,
        importWizardOpen: payload['open']
      };

    case 'SET_IMPORT_PREVIEW':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          importPreview: payload['preview'],
        }
      };

    case 'SET_IMPORT_STATUS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          importStatus: payload['status']
        }
      };

    case 'SET_IMPORT_HISTORY':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          importHistory: payload['history']
        }
      };

    case 'CLEAR_IMPORT_DATA':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          importPreview: null,
          importStatus: null
        }
      };

    case 'SET_FILTERS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          filters: payload['filters']
        }
      };

    case 'SET_QUICK_FILTERS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          quickFilters: payload['filters']
        }
      };

    case 'SET_FILTER':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          filter: payload['filter']
        }
      };

    case 'APPLY_FILTERS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          activeFilters: payload['filters']
        }
      };

    case 'UPDATE_TAG_GLOBALLY': {
      const objState = state[payload['objectType']];
      if (!objState) return state;
      return {
        ...state,
        [payload['objectType']]: {
          ...objState,
          allTags: (objState.allTags || []).map(tag =>
            tag.name === payload['oldTagName']
              ? { name: payload['newTagName'], color: payload['newTagColor'] }
              : tag
          )
        }
      };
    }

    case 'CREATE_TAG': {
      const objState = state[payload['objectType']];
      if (!objState) return state;
      return {
        ...state,
        [payload['objectType']]: {
          ...objState,
          allTags: [...(objState.allTags || []), { name: payload['tagName'], color: payload['tagColor'] }]
        }
      };
    }

    case 'DELETE_TAG_GLOBALLY': {
      const objState = state[payload['objectType']];
      if (!objState) return state;
      return {
        ...state,
        [payload['objectType']]: {
          ...objState,
          allTags: (objState.allTags || []).filter(tag => tag.name !== payload['tagName'])
        }
      };
    }

    case 'CLEAR_FILTERS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          filters: {
            filterGroups: [],
            groupOperator: 'OR'
          },
          activeFilters: null
        }
      };

    // Export cases
    case 'TOGGLE_EXPORT_WIZARD':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          exportWizardOpen: payload['open']
        }
      };

    case 'SET_EXPORT_PREVIEW':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          exportPreview: payload['preview']
        }
      };

    case 'SET_EXPORT_STATUS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          exportStatus: payload['status']
        }
      };

    case 'SET_EXPORT_HISTORY':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          exportHistory: payload['history']
        }
      };

    case 'CLEAR_EXPORT_DATA':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          exportPreview: null,
          exportStatus: null
        }
      };

    case 'SET_VIEWS':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          views: payload['views']
        }
      };

    case 'SET_DEFAULT_VIEW':
      return {
        ...state,
        [payload['objectType']]: {
          ...state[payload['objectType']],
          defaultView: payload['view']
        }
      };

    default:
      return state;
  }
}